import React, { useState } from 'react';
import { Box, Card, CardContent, CardMedia, makeStyles, Typography, Snackbar } from '@material-ui/core';
import ModalComponent from '../MenuItemModal';

const useStyles = makeStyles((theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    backgroundColor: (props: { soldOut: boolean; unavailable: boolean }) =>
      props.unavailable ? '#f5f5f5' : theme.palette.background.paper,
    opacity: (props: { soldOut: boolean; unavailable: boolean }) =>
      props.unavailable ? 0.7 : 1,
    cursor: (props: { soldOut: boolean; unavailable: boolean }) =>
      props.soldOut || props.unavailable ? 'not-allowed' : 'pointer',
    marginBottom: theme.spacing(2),
    width: '100%',
    height: 140,
  },
  cardContent: {
    flex: 1,
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  cardMedia: {
    width: 140,
    height: '100%',
    objectFit: 'cover',
    filter: (props: { soldOut: boolean; unavailable: boolean }) =>
      props.unavailable ? 'grayscale(100%) opacity(50%)' : 'none',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
  soldOutText: {
    color: theme.palette.error.main,
    fontSize: '0.9rem',
    fontWeight: 'bold',
  },
  unavailableText: {
    color: theme.palette.warning.main,
    fontSize: '0.85rem',
    fontWeight: 'bold',
  },
  description: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  price: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
}));

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number; // Price in cents
  imageUrl?: string;
  soldOut: boolean;
  item_per_day_left: number;
  track_stock?: boolean;
  is_available?: boolean;
  modifier_assignments: any[]; // Replace with actual type
  allergy_assignments: any[]; // Replace with actual type
}

interface Cart {
  [key: string]: number;
}

interface MenuCardProps {
  item: MenuItem;
  cart: Cart;
  dispatch: React.Dispatch<Action>;
  primary: string | null;
  secondary: string | null;
}

type Action =
  | { type: 'ADD_TO_CART'; payload: { cartKey: string; quantity: number } }
  | {
      type: 'REMOVE_FROM_CART';
      payload: { cartKey: string };
    };

interface MenuCardProps {
  item: MenuItem;
  cart: Cart;
  dispatch: React.Dispatch<Action>;
  primary: string | null;
  secondary: string | null;
  googleFontFamily?: string;
}

const MenuCard: React.FC<MenuCardProps> = ({ item, cart, dispatch, primary, secondary, googleFontFamily }) => {
  const { id, name, description, price, imageUrl, item_per_day_left, is_available, track_stock } = item;
  const soldOut = track_stock !== false && item_per_day_left === 0;
  const unavailable = is_available === false;
  const classes = useStyles({ soldOut, unavailable });

  const textStyle = {
    fontFamily: googleFontFamily ? `'${googleFontFamily}', sans-serif` : 'inherit',
  };
  const [modalOpen, setModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleOpenModal = () => {
    if (unavailable) {
      setSnackbarOpen(true);
      return;
    }
    if (!soldOut && checkStockAvailability(id, 1)) {
      setModalOpen(true);
    } else {
      setSnackbarOpen(true);
    }
  };

  const handleCloseModal = () => setModalOpen(false);

  // Truncate description to a max of 80 characters for long text
  const truncatedDescription = description.length > 40 ? `${description.slice(0, 40)}...` : description;

  // Check stock availability
  const checkStockAvailability = (productId: string, quantity: number): boolean => {
    // BLUM-5930: untracked items are always available regardless of stock count.
    if (track_stock === false) return true;
    const itemInCartQuantity = Object.keys(cart).reduce((total, cartKey) => {
      const [cartProductId] = cartKey.split(':');
      if (cartProductId === productId) {
        return total + cart[cartKey];
      }
      return total;
    }, 0);

    const totalQuantity = itemInCartQuantity + quantity;
    return totalQuantity <= item_per_day_left;
  };

  const isLongNameSoldOutNoImage = name.length > 25 && checkStockAvailability(id, 1) && imageUrl === '';

  return (
    <>
      <Card className={classes.card} onClick={handleOpenModal}>
        {/* Image Section */}
        {/*{imageUrl ? (*/}
        {/*  <CardMedia className={classes.cardMedia} image={imageUrl} title={name} />*/}
        {/*) : (*/}
        {/*  <div*/}
        {/*    style={{*/}
        {/*      width: 120,*/}
        {/*      height: '100%',*/}
        {/*      // backgroundColor: '#f0f0f0',*/}
        {/*    }}></div>*/}
        {/*)}*/}
        {imageUrl !== '' && <CardMedia className={classes.cardMedia} image={imageUrl} title={name} />}

        {/* Content Section */}
        <CardContent className={classes.cardContent}>
          <Box>
            <Typography variant="h6" className={classes.title}>
              {name}
            </Typography>
            {unavailable && (
              <Typography className={classes.unavailableText}>
                Not available right now
              </Typography>
            )}
            {!unavailable && soldOut && (
              <Typography className={classes.soldOutText} style={{ fontSize: isLongNameSoldOutNoImage ? '0.8rem' : '0.9rem' }}>
                Out of stock
              </Typography>
            )}
            <Typography className={classes.description}>{truncatedDescription}</Typography>
          </Box>
          <Typography className={classes.price}>£{(price / 100).toFixed(2)}</Typography>
        </CardContent>
      </Card>

      {/* Modal for Item Details */}
      {!soldOut && !unavailable && (
        <ModalComponent
          primary={primary}
          secondary={secondary}
          open={modalOpen}
          onClose={handleCloseModal}
          item={item}
          cart={cart}
          dispatch={dispatch}
          googleFontFamily={googleFontFamily}
          textStyle={textStyle}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={unavailable ? 'This item is not available right now.' : 'Item is sold out or exceeds available stock.'}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  );
};

export default MenuCard;
